import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { RefreshCw, Search, X } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/shared/ui/sheet";
import { cn } from "@/shared/lib/utils";
import type { ProductNode } from "../api/productService";
import { PRODUCT_LOAD_FAILED_TRIGGER } from "./productPickCopy";

/**
 * 产品选择抽屉(2026-07-30 D7 抽取)。
 *
 * ═══ 由来 ═══════════════════════════════════════════════════════════════════
 * 2026-07-28 给自由创作做的 `ProductPickerDrawer` 换掉了平铺 `<select>`,理由记在那边:
 * 36 个产品跨品类乱序、没搜索、**选之前看不到这个产品带什么进来**。
 * 长图搭建器(`CanvasIntakePanel`)当时没跟着换,还是一个 37 项的裸 select ——
 * 同一个租户、同一棵产品树、同一个「选产品」动作,两个入口两种体验(W1 走查 D7)。
 *
 * ═══ 为什么抽在 entity 层而不是 poster 里 ════════════════════════════════════
 * 消费方现在是 poster 的两条路(自由创作 / 长图规划),视频那条也迟早要用。
 * 它只认 `ProductNode` 这一种输入、只吐回选中的节点,**不碰任何一个消费方的领域形状**
 * —— 字段怎么映射(`freeCreateProductFromNode` vs `planProductFromNode`)由各自的适配器管,
 * 那两份故意不同(一边按 ai schema 截断,一边不截断)。抽的是选择动作,不是数据契约。
 *
 * 可选性判据(`pickableProducts`)留在调用方:两条路都用同一份,但那是**业务口径**
 * (internal 节点的 attributes 里有成本价,不能进对客的图),不该被一个 UI 组件默默替用户决定。
 */

export interface ProductPickSheetProps {
  /** 已按业务口径筛过的可选产品(调用方过 `pickableProducts`)。 */
  products: ProductNode[];
  /** 全量节点 —— 只用来把 parent code 翻成品类名做分组;不传就全归「未分类」。 */
  allNodes?: ProductNode[];
  /** 当前选中产品的 code;空串/undefined = 未选。 */
  pickedCode?: string;
  /** 选中(或点「不选」传 null)。 */
  onPick: (node: ProductNode | null) => void;
  disabled?: boolean;
  loading?: boolean;
  /** 触发按钮上的文案;不传按「从 N 个产品里选 / 已选:X」自动生成。 */
  triggerLabel?: string;
  /** 底部说明行;不传按「已选:X · N 条卖点」自动生成。 */
  footerHint?: string;
  /**
   * 多选(2026-08-19 视频线接入)。**加性的** —— 不传就是原来的单选,海报 / 长图 / 记者
   * 三条既有调用方一行都不用改。
   *
   * 视频线一条片可以同时带多个产品(军师那边 selling_points 本来就是多条),而它原来
   * 那份自造的平铺网格是单选且点一下就收起,想选第二个得重新打开、且**没有已选标记**。
   */
  multi?: boolean;
  /** 多选时的已选 code 集合。 */
  pickedCodes?: string[];
  /** 多选时点某一项(选中 / 取消都走它,调用方自己 toggle)。 */
  onToggle?: (node: ProductNode) => void;
  /**
   * 抽屉被打开时通知一次 —— 给**懒加载**产品树的调用方用(视频线是点开才拉,
   * 不在页面挂载时就打一次 /tenant/products)。
   */
  onOpen?: () => void;
  /**
   * 完全自定义触发元素(必须是单个可接 ref 的元素,走 `SheetTrigger asChild`)。
   *
   * 视频线的素材行里那颗按钮有自己的紧凑样式,换抽屉不该顺手把那一行的排版也改了 ——
   * **抽的是选择动作,不是按钮长相**(与本组件头注同一口径)。不传就用默认 outline 按钮。
   */
  trigger?: ReactNode;
  /**
   * 挂载即打开一次(1013 V6:视频舱「从产品库开始」落创建页直接弹出选品抽屉)。
   * 只在首次挂载生效,之后开关全由用户操作;同样触发 onOpen(懒加载产品树)。
   */
  autoOpen?: boolean;
  /**
   * 每一项后面附加一条**调用方口径**的标注。
   *
   * 视频线要标「只带卖点(没挂图)」—— 该品在产品树上没有 sampleImages,选了它成片里不会出现
   * 真实产品图。这是视频线特有的关切(海报线自己会传图),所以由调用方给,
   * 本组件不去认 `sampleImages` —— 与头注同一口径:**抽的是选择动作,不是数据契约**。
   */
  renderBadge?: (node: ProductNode) => ReactNode;
  /**
   * 产品库接口失败(0926 修复波第三批)。
   *
   * ★此前失败与「真没有在售产品」共用同一个空态(「产品库暂无在售产品」)—— 接口抖一下,
   *   用户就以为自己的产品库是空的,会跑去重建产品。失败要**说是失败**,并给「重试」。
   */
  error?: boolean;
  /** 失败时「重试」按钮调它(一般是 useQuery 的 refetch)。 */
  onRetry?: () => void;
}

export function ProductPickSheet({
  products,
  allNodes = [],
  pickedCode,
  onPick,
  disabled,
  loading,
  triggerLabel,
  footerHint,
  multi,
  pickedCodes,
  onToggle,
  onOpen,
  trigger,
  renderBadge,
  error,
  onRetry,
  autoOpen,
}: ProductPickSheetProps) {
  const [open, setOpen] = useState(false);
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!autoOpen || autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    setOpen(true);
    onOpen?.();
  }, [autoOpen, onOpen]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<string | null>(null);

  const picked = useMemo(
    () => (pickedCode ? (products.find((p) => p.code === pickedCode) ?? null) : null),
    [products, pickedCode]
  );

  /** 品类 = 产品的父节点(带计数);没有父节点的归「未分类」。 */
  const categories = useMemo(() => {
    const byCode = new Map(allNodes.map((n) => [n.code, n]));
    const counter = new Map<string, { name: string; n: number }>();
    for (const p of products) {
      const parent = p.parent ? byCode.get(p.parent) : undefined;
      const key = parent?.code ?? "_none";
      const name = parent?.name ?? "未分类";
      const cur = counter.get(key);
      counter.set(key, { name, n: (cur?.n ?? 0) + 1 });
    }
    return [...counter.entries()].map(([code, v]) => ({ code, ...v }));
  }, [allNodes, products]);

  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return products.filter((p) => {
      if (cat && (p.parent ?? "_none") !== cat) return false;
      if (!kw) return true;
      // ★搜索匹配名称 + 卖点 + 别名 —— 用户记得住的往往是卖点("零糖")不是全名
      const hay = [p.name, p.summary ?? "", ...(p.sellingPoints ?? []), ...(p.aliases ?? [])]
        .join(" ")
        .toLowerCase();
      return hay.includes(kw);
    });
  }, [products, q, cat]);

  const codes = pickedCodes ?? [];
  const isPicked = (n: ProductNode) => (multi ? codes.includes(n.code) : picked?.code === n.code);

  const pick = (n: ProductNode | null) => {
    // ★多选**不关抽屉**:关了的话选第二个要重新打开一次,正是视频线那份自造网格的毛病。
    if (multi && n) {
      onToggle?.(n);
      return;
    }
    onPick(n);
    setOpen(false);
  };

  // ★失败只在「一个产品都没拿到」时接管空态;有缓存数据时照常可选(后台刷新失败不打扰)。
  const failed = !!error && products.length === 0;

  const autoTrigger = loading
    ? "读取产品库…"
    : failed
      ? PRODUCT_LOAD_FAILED_TRIGGER
      : multi
        ? codes.length
          ? `已选 ${codes.length} 个产品`
          : products.length
            ? `从 ${products.length} 个产品里选(可多选)`
            : "产品库暂无在售产品"
        : picked
          ? `已选:${picked.name}`
          : products.length
            ? `从 ${products.length} 个产品里选`
            : "产品库暂无在售产品";

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) onOpen?.();
      }}
    >
      <SheetTrigger asChild>
        {trigger ?? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || loading}
            className="h-8 gap-1.5 text-xs"
          >
            <Search className="h-3.5 w-3.5" />
            {triggerLabel ?? autoTrigger}
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
        <SheetHeader className="border-b border-border/60 px-4 py-3">
          <SheetTitle className="text-sm">选产品</SheetTitle>
        </SheetHeader>

        <div className="border-b border-border/60 px-4 py-2.5">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="搜产品名 / 卖点 / 别名 …"
            className="h-8 text-xs"
          />
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-[124px_minmax(0,1fr)]">
          <div className="overflow-y-auto border-r border-border/60 p-2">
            <CatBtn active={cat === null} onClick={() => setCat(null)}>
              全部 ({products.length})
            </CatBtn>
            {categories.map((c) => (
              <CatBtn key={c.code} active={cat === c.code} onClick={() => setCat(c.code)}>
                {c.name} ({c.n})
              </CatBtn>
            ))}
          </div>

          <div className="overflow-y-auto p-2">
            {failed ? (
              <div
                data-testid="product-pick-error"
                className="flex flex-col items-center gap-2 px-2 py-6 text-center text-xs text-muted-foreground"
              >
                <p>产品库没读到(网络或服务出错),不是没有产品。</p>
                {onRetry && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    onClick={onRetry}
                  >
                    <RefreshCw className="h-3 w-3" />
                    重试
                  </Button>
                )}
              </div>
            ) : filtered.length === 0 ? (
              <p className="px-2 py-6 text-center text-xs text-muted-foreground">
                {products.length === 0 ? "产品库暂无在售产品" : "没有匹配的产品"}
              </p>
            ) : (
              filtered.map((n) => {
                const active = isPicked(n);
                return (
                  <button
                    key={n.code}
                    type="button"
                    onClick={() => pick(n)}
                    data-testid={`product-pick-${n.code}`}
                    /* ★已选标记要**机器也读得到**:视频线那份自造网格重新打开时 19 个卡片
                       零标记,用户只能靠记忆选第二个。判据钉这个属性,改样式打不坏它。 */
                    data-picked={active ? "true" : "false"}
                    aria-pressed={multi ? active : undefined}
                    className={cn(
                      "mb-1.5 w-full rounded-md border px-2.5 py-2 text-left transition-colors",
                      active
                        ? "border-primary bg-primary/10"
                        : "border-border/60 bg-background/40 hover:border-primary/40"
                    )}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-xs font-semibold">{n.name}</span>
                      {renderBadge?.(n)}
                    </div>
                    {/* ★选之前就看到带什么进来 —— 老 select 只有名字,选完才知道 */}
                    {n.summary && (
                      <div className="truncate text-2xs text-muted-foreground">{n.summary}</div>
                    )}
                    {!!n.sellingPoints?.length && (
                      <div className="mt-0.5 truncate text-2xs text-muted-foreground">
                        {n.sellingPoints.slice(0, 3).join(" · ")}
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border/60 px-4 py-2.5">
          <span className="min-w-0 truncate text-2xs text-muted-foreground">
            {footerHint ??
              (picked
                ? `已选:${picked.name} · ${picked.sellingPoints?.length ?? 0} 条卖点`
                : "未选(可以不选,自己写文案)")}
          </span>
          {picked && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 gap-1 text-xs text-muted-foreground"
              onClick={() => pick(null)}
            >
              <X className="h-3 w-3" />
              不选
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CatBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "mb-1 w-full truncate rounded-md px-2 py-1.5 text-left text-2xs transition-colors",
        active ? "bg-primary/10 font-semibold text-primary" : "text-muted-foreground hover:bg-muted"
      )}
    >
      {children}
    </button>
  );
}
